import { EditIcon } from "@xola/icons";
import { Input } from "@xola/ui-kit";
import { FormField, isPurchaseEmailRequired, isValidEmail, SecondaryButton, SwitchField } from "common";
import { useField } from "formik";
import React, { useEffect } from "react";
import { FieldType } from "types";
import shallow from "zustand/shallow";
import { debouncedSyncGuestFormResponse } from "./helpers/PurchaseCreateHelpers";
import { useGiftOnlyOrganizerRequirement } from "./hooks/useGiftDetailsRequirements";
import { useCustomerDetailsStore } from "./store/customerDetailsStore";
import { useSeller } from "@/hooks/useAccount";
import { isOrganizerEmailRequirementEnabled } from "@/models/GiftPreference";
import { isMembershipItem } from "@/models/PurchaseItem";
import { useCreatePurchaseStore } from "@/stores/createPurchaseStore";

export const CreatePurchaseEmail = React.memo(({ onEditContactClick }: { onEditContactClick: () => void }) => {
    const seller = useSeller();
    const [field, , helpers] = useField("customerEmail");

    const [organizer, form] = useCreatePurchaseStore((state) => [state.organizer, state.getForm()], shallow);
    const [emailRequired, isSameAsRecipient] = useCustomerDetailsStore(
        (state) => [state.emailRequired, state.isSameAsRecipient],
        shallow,
    );

    const hasMembershipItem = useCreatePurchaseStore.getState().purchase.items.some((item) => isMembershipItem(item));
    const giftOrganizerRequirement = useGiftOnlyOrganizerRequirement();
    const requireEmailEnabled = isOrganizerEmailRequirementEnabled(
        giftOrganizerRequirement,
        isPurchaseEmailRequired(seller),
    );
    const isEmailRequired = requireEmailEnabled && !hasMembershipItem ? emailRequired : null;

    const disabled = !!organizer;
    const isInputLocked = disabled || isSameAsRecipient;
    const isEmailSwitchedOff = giftOrganizerRequirement !== null && isEmailRequired !== null && !emailRequired;

    useEffect(() => {
        if (organizer?.email) {
            helpers.setValue(organizer.email);
        }
        // Not adding helpers in the dependency array as it is coming from formik and will always change even when some other field changes and it can cause infinite loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizer?.email]);

    useEffect(() => {
        useCustomerDetailsStore.setState({
            emailRequired: requireEmailEnabled ? emailRequired : false,
        });
    }, [requireEmailEnabled, emailRequired]);

    const setCustomerEmailToPurchase = (e: any) => {
        helpers.setValue(e.target.value);
    };

    const updateCustomerEmailInDraftPurchase = (e: any) => {
        //check if the value is a email format before updating the store
        if (!e.target.value || !isValidEmail(e.target.value)) {
            return;
        }
        useCreatePurchaseStore.getState().actions.setPurchase((draft) => {
            draft.customerEmail = e.target.value;
        });
    };

    useEffect(() => {
        debouncedSyncGuestFormResponse(FieldType.EMAIL, field.value, form);
    }, [field.value, form]);

    return (
        <FormField
            preventLabelClick
            name="customerEmail"
            label={
                <CustomerEmailLabel
                    isRequired={isEmailRequired}
                    isDisabled={isInputLocked}
                    isChecked={!!emailRequired}
                />
            }
        >
            <div className="flex items-center space-x-2">
                <Input
                    disabled={isInputLocked || isEmailSwitchedOff}
                    name="customerEmail"
                    label="Email"
                    placeholder="Email"
                    value={field.value}
                    isRequired={isEmailRequired}
                    onChange={setCustomerEmailToPurchase}
                    onBlur={updateCustomerEmailInDraftPurchase}
                />

                {disabled && (
                    <SecondaryButton icon={<EditIcon />} onClick={onEditContactClick}>
                        Edit
                    </SecondaryButton>
                )}
            </div>
        </FormField>
    );
});

interface CustomerEmailLabelProps {
    isChecked: boolean;
    isRequired: boolean | null;
    isDisabled: boolean;
}

const CustomerEmailLabel = ({ isRequired, isDisabled, isChecked }: CustomerEmailLabelProps) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [field, meta, helpers] = useField("customerEmail");

    const handleChange = (emailRequired: boolean) => {
        useCustomerDetailsStore.getState().actions.setEmailRequired(emailRequired);

        if (!emailRequired) {
            helpers.setValue("");
            helpers.setError("");
            useCustomerDetailsStore.setState({ customerEmail: "" });
        }
    };

    if (isRequired === null || isDisabled) {
        return <>Email</>;
    }

    return (
        <SwitchField size="small" font="font-bold" direction="left" isChecked={isChecked} onChange={handleChange}>
            <span className="font-bold">Email</span>
        </SwitchField>
    );
};

CreatePurchaseEmail.displayName = "CreatePurchaseEmail";
